import type { RumEvent } from "@datadog/browser-rum";

/**
 * Drops RUM errors that come from outside our code:
 *   1. Opaque `"Script error."`, which browsers report for cross-origin scripts.
 *      Our bundles are same-origin, so it's never ours. This also hides Payrix
 *      errors raised outside Angular's zone (api.payrix.com doesn't allow CORS);
 *      those carry no message or stack anyway.
 *   2. A browser extension or reCAPTCHA as the top stack frame. Network
 *      failures are kept: an extension's fetch wrapper can sit on top of our
 *      own request.
 *   3. Known messages from link scanners, extensions and iOS webviews.
 */

const OPAQUE_SCRIPT_ERROR_PATTERN = /^(?:uncaught\s+)?"?script error\.?"?$/i;

const THIRD_PARTY_TOP_FRAME_PATTERN =
  /(?:chrome-extension|moz-extension|safari-extension|safari-web-extension|webkit-masked-url):\/\/|https:\/\/www\.(?:gstatic|google)\.com\/recaptcha\//i;

const NETWORK_FAILURE_PATTERN =
  /failed to fetch|networkerror when attempting to fetch resource|load failed/i;

const THIRD_PARTY_MESSAGE_PATTERNS: readonly RegExp[] = [
  // Outlook SafeLinks scanner opening an emailed link.
  /^Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/,
  // Extension APIs.
  /runtime\.sendMessage\(\)\. Tab not found/,
  /^No Listener: tabs:/,
  /\bi18n\.detectLanguage is not a function/,
  // iOS in-app browser bridges. Our code never calls `webkit.messageHandlers`.
  /\bwebkit\.messageHandlers\b/,
  /^WKWebView API client did not respond to this postMessage$/,
];

export function isThirdPartyRumNoise(
  event: RumEvent | null | undefined,
): boolean {
  if (event?.type !== "error") {
    return false;
  }

  const message = event.error?.message ?? "";
  if (OPAQUE_SCRIPT_ERROR_PATTERN.test(message)) {
    return true;
  }

  if (
    THIRD_PARTY_TOP_FRAME_PATTERN.test(topStackFrame(event.error?.stack)) &&
    !NETWORK_FAILURE_PATTERN.test(message)
  ) {
    return true;
  }

  return THIRD_PARTY_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
}

function topStackFrame(stack: string | undefined): string {
  return stack?.split("\n").find((line) => /^\s*at\s/.test(line)) ?? "";
}
